
import { Component, Vue, Watch } from 'vue-property-decorator';
import { ValidationObserver } from 'vee-validate';
import { DateTime } from 'luxon';

import { sharedState } from '../../../../framework/state';
import { getResource, postResource } from '../../../../framework/client/resource';
import { getEnumerationOptions } from '../../../../framework/service/options';
import { getLastDateOfMonth, getMonth, getYear } from '../../../../../common/framework/util/time';
import { dateToDbDateString } from '../../../../../common/framework/util/convert';

import TextField from '../../../../framework/fields/TextField.vue';
import SelectField from '../../../../framework/fields/SelectField.vue';
import SwitchField from '../../../../framework/fields/SwitchField.vue';
import NumberField from '../../../../framework/fields/NumberField.vue';
import DateField from '../../../../framework/fields/DateField.vue';
import PercentageField from '../../../../framework/fields/PercentageField.vue';
import CurrencyAmountField from '../../../../framework/fields/CurrencyAmountField.vue';
import AddOrderVendorContract from './AddOrderVendorContract.vue';
import EmailField from '../../../../framework/fields/EmailField.vue';

import MonthsField from '../../../fields/MonthsField.vue';
import UsageField from '../../../fields/UsageField.vue';
import AutoCompleteMultipleField from '../../../fields/AutoCompleteMultipleField.vue';

import { VehicleType } from '../../../../../common/application/model/enums/VehicleType';
import { VehicleContract } from '../../../../../common/application/model/VehicleContract';
import { VehicleState } from '../../../../../common/application/model/enums/VehicleState';
import { VehicleIntendedUse } from '../../../../../common/application/model/enums/VehicleIntendedUse';
import { VehicleUsageType } from '../../../../../common/application/model/enums/VehicleUsageType';
import { VehicleEnginePowerType } from '../../../../../common/application/model/enums/VehicleEnginePowerType';
import { VehicleElectricalClass } from '../../../../../common/application/model/enums/VehicleElectricalClass';
import { FinancingContractType } from '../../../../../common/application/model/enums/FinancingContractType';
import { AccountType } from '../../../../../common/application/model/enums/AccountType';
import { SmartFleetContractType } from '../../../../../common/application/model/enums/SmartFleetContractType';
import { ApplicationResource } from '../../../../../common/application/enumeration/ApplicationResource';
import { VehicleMake } from '../../../../../common/application/model/VehicleMake';
import { VehicleMakeType } from '../../../../../common/application/model/enums/VehicleMakeType';

import { getAccountOptions, getCustomerOptions, getVendorOptions } from '../../../service/application_options_service';
import { loadingEnd, loadingStart } from '../../../../main';
import { errorToast } from '../../../service/toast_service';

@Component({
    components: {
        AddOrderVendorContract,
        MonthsField,
        UsageField,
        PercentageField,
        DateField,
        NumberField,
        SwitchField,
        SelectField,
        TextField,
        CurrencyAmountField,
        ValidationObserver,
        EmailField,
        AutoCompleteMultipleField,
    },
})
export default class AddOrder extends Vue {
    // Constants
    readonly resourceType = ApplicationResource.VEHICLE_CONTRACT;

    // Properties

    // Refs
    $refs!: {
        financingContract: AddOrderVendorContract;
        insuranceContract: AddOrderVendorContract;
        maintenanceContract: AddOrderVendorContract;
        importerCareSupportContract: AddOrderVendorContract;
        tireStorageContract: AddOrderVendorContract;
        fuelCardContract: AddOrderVendorContract;
        substituteVehicleContract: AddOrderVendorContract;
        vehicleTrackingContract: AddOrderVendorContract;
        washingContract: AddOrderVendorContract;
        vehicleTaxContract: AddOrderVendorContract;
        tireRenewalContract: AddOrderVendorContract;

        observer: Vue;
    };
    // State
    shared = sharedState;
    local = {
        row: {} as VehicleContract,
        customerOptions: [] as { id: string | undefined; label: string }[],
        vehicleTypeOptions: [] as { id: string | undefined; label: string }[],
        vehicleStateOptions: [] as { id: string | undefined; label: string }[],
        vehicleIntendedUseOptions: [] as { id: string | undefined; label: string }[],
        vehicleUsageTypeOptions: [] as { id: string | undefined; label: string }[],
        vehicleEnginePowerTypeOptions: [] as { id: string | undefined; label: string }[],
        vehicleElectricalClassOptions: [] as { id: string | undefined; label: string }[],

        financingContractTypeOptions: [] as { id: string | undefined; label: string }[],

        smartFleetContractTypeOptions: [] as { id: string | undefined; label: string }[],

        customerExpenseAccountOptions: [] as { id: string | undefined; label: string }[],

        vendorOptions: [] as { id: string | undefined; label: string }[],

        showAllVehicleFields: false,
        showAllSmartFleetFields: false,
        showAllFinancingFields: false,
        vehicleMakeId: [] as string[],
    };

    // Functions
    async mounted() {
        this.local.customerOptions = await getCustomerOptions();

        this.local.vehicleTypeOptions = getEnumerationOptions('VehicleType', Object.keys(VehicleType), this);
        this.local.vehicleStateOptions = getEnumerationOptions('VehicleState', Object.keys(VehicleState), this);
        this.local.vehicleIntendedUseOptions = getEnumerationOptions(
            'VehicleIntendedUse',
            Object.keys(VehicleIntendedUse),
            this,
        );
        this.local.vehicleUsageTypeOptions = getEnumerationOptions(
            'VehicleUsageType',
            Object.keys(VehicleUsageType),
            this,
        );
        this.local.vehicleEnginePowerTypeOptions = getEnumerationOptions(
            'VehicleEnginePowerType',
            Object.keys(VehicleEnginePowerType),
            this,
        );
        this.local.vehicleElectricalClassOptions = getEnumerationOptions(
            'VehicleElectricalClass',
            Object.keys(VehicleElectricalClass),
            this,
        );

        this.local.financingContractTypeOptions = getEnumerationOptions(
            'FinancingContractType',
            Object.keys(FinancingContractType),
            this,
        );
        this.local.smartFleetContractTypeOptions = getEnumerationOptions(
            'SmartFleetContractType',
            Object.keys(SmartFleetContractType),
            this,
        );

        this.local.vendorOptions = await getVendorOptions();
    }

    @Watch('local.row.customerId')
    async customerIdChange() {
        if (!this.local.row.customerId) {
            return;
        }

        this.local.customerExpenseAccountOptions = await getAccountOptions(
            this.local.row.customerId,
            AccountType.EXPENSE,
        );
    }

    @Watch('local.row.smartFleetContractStart')
    async smartFleetContractStartChange(smartFleetContractStart: Date) {
        this.local.row = {
            ...this.local.row,
            financingContractStart: smartFleetContractStart,
        };
    }

    @Watch('local.row.smartFleetContractPeriodMonths')
    async smartFleetContractPeriodMonthsChange(smartFleetContractPeriodMonths: number) {
        if (this.local.row.smartFleetContractStart && smartFleetContractPeriodMonths) {
            const startDate = new Date(this.local.row.smartFleetContractStart);
            this.local.row = {
                ...this.local.row,
                smartFleetContractEnd: dateToDbDateString(
                    getLastDateOfMonth(getYear(startDate), getMonth(startDate) + smartFleetContractPeriodMonths),
                ) as any,
            };
        }
        this.local.row = {
            ...this.local.row,
            financingContractPeriodMonths: smartFleetContractPeriodMonths,
        };
    }

    @Watch('local.row.smartFleetContractEnd')
    async smartFleetContractEndChange(smartFleetContractEnd: Date) {
        this.local.row = {
            ...this.local.row,
            financingContractEnd: smartFleetContractEnd,
        };
    }

    @Watch('local.row.smartFleetContractInitialUsage')
    async smartFleetContractInitialUsageChange(smartFleetContractInitialUsage: number) {
        this.local.row = {
            ...this.local.row,
            financingContractInitialUsage: smartFleetContractInitialUsage,
        };
    }

    @Watch('local.row.smartFleetContractUsageBudget')
    async smartFleetContractUsageBudgetChange(smartFleetContractUsageBudget: number) {
        this.local.row = {
            ...this.local.row,
            financingContractUsageBudget: smartFleetContractUsageBudget,
        };
    }

    @Watch('local.row.financingContractPeriodMonths')
    async financingContractPeriodMonthsChange(financingContractPeriodMonths: number) {
        if (this.local.row.financingContractStart && financingContractPeriodMonths) {
            const startDate = new Date(this.local.row.financingContractStart);
            this.local.row = {
                ...this.local.row,
                financingContractEnd: dateToDbDateString(
                    getLastDateOfMonth(getYear(startDate), getMonth(startDate) + financingContractPeriodMonths),
                ) as any,
            };
        }
    }

    dropdownClosed(vehicleMakeIds: string[], idField: string): void {
        if (!vehicleMakeIds[0]) {
            return;
        }

        this.local.row = {
            ...this.local.row,
            vehicleMakeId: vehicleMakeIds[0],
        };
    }

    copyDateValues(source: string) {
        if (source === 'smartfleet') {
            this.local.row = {
                ...this.local.row,
                financingContractStart: this.local.row.smartFleetContractStart,
                financingContractEnd: this.local.row.smartFleetContractEnd,
                financingContractPeriodMonths: this.local.row.smartFleetContractPeriodMonths,
            };
        } else {
            this.local.row = {
                ...this.local.row,
                smartFleetContractStart: this.local.row.financingContractStart,
                smartFleetContractEnd: this.local.row.financingContractEnd,
                smartFleetContractPeriodMonths: this.local.row.financingContractPeriodMonths,
            };
        }
    }

    async validate(): Promise<boolean> {
        return await (this.$refs.observer as any).validate();
    }

    async validateBeforeSubmit() {
        if (!this.local.row.smartFleetContractHandoverDate) {
            this.local.row = {
                ...this.local.row,
                smartFleetContractHandoverDate: this.local.row.smartFleetContractStart,
            };
        }

        loadingStart(this);
        setTimeout(async () => {
            let valid = await this.validate();
            valid = (await (this.$refs.financingContract as any).validate()) && valid;
            valid = (await (this.$refs.insuranceContract as any).validate()) && valid;
            valid = (await (this.$refs.maintenanceContract as any).validate()) && valid;
            valid = (await (this.$refs.importerCareSupportContract as any).validate()) && valid;
            valid = (await (this.$refs.tireStorageContract as any).validate()) && valid;
            valid = (await (this.$refs.fuelCardContract as any).validate()) && valid;
            valid = (await (this.$refs.substituteVehicleContract as any).validate()) && valid;
            valid = (await (this.$refs.vehicleTrackingContract as any).validate()) && valid;
            valid = (await (this.$refs.washingContract as any).validate()) && valid;
            valid = (await (this.$refs.vehicleTaxContract as any).validate()) && valid;
            valid = (await (this.$refs.tireRenewalContract as any).validate()) && valid;
            if (valid) {
                const vehicleMake = (await getResource<VehicleMake>(
                    ApplicationResource.VEHICLE_MAKE,
                    this.local.row.vehicleMakeId,
                ))!!;
                this.local.row = await postResource<VehicleContract>(this.resourceType, {
                    ...this.local.row,
                    vehicleMake: vehicleMake.name,
                });
                await (this.$refs.financingContract as any).add(this.local.row.id);
                await (this.$refs.insuranceContract as any).add(this.local.row.id);
                await (this.$refs.maintenanceContract as any).add(this.local.row.id);
                await (this.$refs.importerCareSupportContract as any).add(this.local.row.id);
                await (this.$refs.tireStorageContract as any).add(this.local.row.id);
                await (this.$refs.fuelCardContract as any).add(this.local.row.id);
                await (this.$refs.substituteVehicleContract as any).add(this.local.row.id);
                await (this.$refs.vehicleTrackingContract as any).add(this.local.row.id);
                await (this.$refs.washingContract as any).add(this.local.row.id);
                await (this.$refs.vehicleTaxContract as any).add(this.local.row.id);
                await (this.$refs.tireRenewalContract as any).add(this.local.row.id);
                this.back();
            }
            loadingEnd();
        }, 100);
    }

    back() {
        this.$router.go(-1);
    }

    get financingContractStart(): string {
        if (!this.local.row.financingContractStart) {
            return DateTime.now().plus({ months: 1 }).startOf('month').toISO()!!;
        }
        return this.local.row.financingContractStart.toISOString();
    }

    get financingContractEnd(): string {
        if (!this.local.row.financingContractEnd) {
            return DateTime.now().plus({ years: 3 }).endOf('month').toISO()!!;
        }
        return this.local.row.financingContractEnd.toISOString();
    }

    get smartFleetContractStart(): string {
        if (!this.local.row.smartFleetContractStart) {
            return DateTime.now().plus({ months: 1 }).startOf('month').toISO()!!;
        }
        return this.local.row.smartFleetContractStart.toISOString();
    }

    get smartFleetContractEnd(): string {
        if (!this.local.row.smartFleetContractEnd) {
            return DateTime.now().plus({ years: 3 }).endOf('month').toISO()!!;
        }
        return this.local.row.smartFleetContractEnd.toISOString();
    }

    get vehicleMakeId(): string[] {
        return this.local.row.vehicleMakeId ? [this.local.row.vehicleMakeId] : [];
    }

    async addVehicleMake(name: string) {
        try {
            const newVehicleMake: VehicleMake = {
                id: '',
                created: new Date(),
                modified: new Date(),
                name,
                description: '',
                type: VehicleMakeType.CAR,
            };

            const vehicleMake = await postResource<VehicleMake>(ApplicationResource.VEHICLE_MAKE, newVehicleMake);

            setTimeout(() => (this.local.vehicleMakeId = [vehicleMake.id]), 1000);
        } catch (e) {
            errorToast(this, 'error.couldNotInsertVehicleMake');
        }
    }
}
